import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Hats } from '../../../@resources/hats';
import { srand, useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Matrix } from '../../../platform/geom/Matrix';
import { Point } from '../../../platform/geom/Point';
import { Fill } from '../../../ui/Fill';
import { Origin } from '../../canvas/visualization/Origin';
import { useResolve } from '../../Container';
import { HatController } from './HatController';
import { userStealHat } from './HatsCommands';
import { HatSettings, HatThrowInstr, HattingState } from './HatTypes';

const Wrapper = styled.div`
    .hat-throw {
        position: absolute;
        pointer-events: all;
        cursor: pointer;
        & > * {
            position: absolute;
            left: 0;
            top: 0;
            transform: translate(-50%, -50%);
            width: 40px;
            height: 40px;
            background: no-repeat center center;
            background-size: contain;
        }
    }
`;

const renderThrows = (hatting: HattingState, wrapper: HTMLDivElement, animate: boolean, onClick: any) => {

    const throws = Object.values(hatting ?? {})
        .filter(x => !!x && x[0] === 'throw')
        .sort((a, b) => a[3].localeCompare(b[3])) as HatThrowInstr[];

    const lookup = new Set(throws.map(x => x[3]));

    // Remove dead
    for (const child of wrapper.children) {
        if (!child.classList.contains('hat-throw')) continue;
        const id = child.id.slice(1);
        if (lookup.has(id)) continue;
        wrapper.removeChild(child);
    }

    // Add missing
    for (const thr of throws) {
        renderThrow(wrapper, thr, animate, onClick);
    }
};

const renderThrow = (wrapper: HTMLDivElement, data: HatThrowInstr, animate: boolean, onClick: any) => {

    const [_, type, ab, id] = data;
    if (!type) return;

    const elmtId = `h${id}`;
    if (!!wrapper.querySelector(`#${elmtId}`)) return;

    const a = Point.fromBuffer(ab, 0);
    const b = Point.fromBuffer(ab, 2);
    const url = Hats[type];
    const { scale: s } = HatSettings.lookup(type);

    const ba = Point.vector(b, a);
    const power = ba.length() * 10;
    const dist = power / Math.log10(power);

    const from = Matrix.translate(b).scale(s);
    const to = Matrix.translate(b).translate(ba.normalize().multiply(dist)).scale(s);

    const elmt = document.createElement('div');
    wrapper.appendChild(elmt);

    elmt.id = `h${id}`;
    elmt.addEventListener('click', () => onClick(id));
    elmt.className = 'hat-throw';
    elmt.innerHTML = `<div style="background-image: url(${url})"></div>`;
    elmt.style.transform = from.toCSS();
    elmt.animate([
        { transform: from.toCSS(), },
        { transform: `${to.toCSS()} rotate(${dist * srand(id)}deg)` },
    ], {
        duration: animate ? 250 + (dist / Math.log10(dist)) : 0,
        easing: 'cubic-bezier(0,1,.8,1)',
        fill: 'forwards',
    });
};

export const HatLayer = React.memo(() => {

    const dispatch = useResolve(CommandDispatcher);
    const wrapper = useRef<HTMLDivElement>();
    const start = useRef<number>(performance.now());
    const hatting = useObserver(useResolve(HatController).getStore());

    const onHatClick = (throwId: string) => void (dispatch(userStealHat, [throwId]));

    useEffect(() => {
        if (!wrapper.current || !hatting) return;
        renderThrows(
            hatting,
            wrapper.current,
            (performance.now() - start.current) > 500,
            onHatClick
        );
    }, [wrapper.current, hatting]);

    return <Fill id="hat-layer" pointerEvents="none" zIndex="1">
        <Origin>
            <Wrapper ref={wrapper}></Wrapper>
        </Origin>
    </Fill>;
});


